import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TopicCard from "@/components/feature/TopicCard";
import usePageMeta from "@/hooks/usePageMeta";
import {
  formatCount,
  formatHeat,
  getCategoryBySlug,
  getRankedTopics,
  getRelatedTopics,
  getTopicById,
  getTrendMeta,
} from "@/utils/content";

const SITE_URL = "https://hshplx.cn";

export default function TopicPage() {
  const { id = "" } = useParams();
  const topic = getTopicById(id);
  const category = topic ? getCategoryBySlug(topic.categorySlug) : undefined;
  const related = topic ? getRelatedTopics(topic, 3) : [];
  const hotList = getRankedTopics("day").slice(0, 5);
  const trend = topic ? getTrendMeta(topic.trend) : null;

  usePageMeta({
    title: topic ? `${topic.title} | 瓜田热榜` : "话题不存在 - 瓜田热榜",
    description: topic
      ? `${topic.summary}包含事件脉络梳理、热议要点与相关话题推荐。`
      : "该话题页面不存在，返回首页浏览今日热门吃瓜榜。",
    keywords: topic
      ? [...topic.tags, "热门吃瓜", category?.name ?? "全网热议"].join(",")
      : "热门吃瓜,今日热门吃瓜榜",
    canonical: `${SITE_URL}/topic/${id}`,
    schema:
      topic && category
        ? [
            {
              "@context": "https://schema.org",
              "@type": "Article",
              headline: topic.title,
              description: topic.summary,
              image: topic.image,
              datePublished: topic.publishedAt.replace(" ", "T"),
              inLanguage: "zh-CN",
              articleSection: category.name,
              keywords: topic.tags.join(","),
              author: { "@type": "Organization", name: "瓜田热榜" },
              publisher: { "@type": "Organization", name: "瓜田热榜" },
              mainEntityOfPage: `${SITE_URL}/topic/${id}`,
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
                { "@type": "ListItem", position: 2, name: "今日热门吃瓜榜", item: `${SITE_URL}/rank` },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: category.name,
                  item: `${SITE_URL}/category/${category.slug}`,
                },
                { "@type": "ListItem", position: 4, name: topic.title, item: `${SITE_URL}/topic/${id}` },
              ],
            },
          ]
        : undefined,
  });

  if (!topic || !category) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="max-w-md text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-file-unknow-line text-2xl leading-none" aria-hidden="true" />
            </span>
            <h1 className="heading-font mt-5 text-2xl font-black text-foreground-950">这个话题找不到了</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              话题可能已经被合并或下榜。去榜单页看看现在正在发酵的新瓜吧。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/rank"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看今日热榜
              </Link>
              <Link
                to="/"
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <Breadcrumb items={[{ label: category.name, to: `/category/${category.slug}` }, { label: topic.title }]} />

      <main className="w-full">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <article className="w-full lg:col-span-8">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Link
                  to={`/category/${category.slug}`}
                  className="flex cursor-pointer items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1.5 font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
                >
                  <i className={`${category.icon} text-sm leading-none`} aria-hidden="true" />
                  {category.name}
                </Link>
                {trend && (
                  <span className={`flex items-center gap-1 rounded-full px-3 py-1.5 ${trend.className}`}>
                    <i className={`${trend.icon} text-sm leading-none`} aria-hidden="true" />
                    {trend.text}
                  </span>
                )}
                <span className="flex items-center gap-1 rounded-full bg-background-200 px-3 py-1.5 text-foreground-700">
                  <i className="ri-fire-line text-sm leading-none" aria-hidden="true" />
                  {formatHeat(topic.heat)} 热度
                </span>
              </div>

              <h1 className="heading-font mt-4 text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                {topic.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-time-line text-base leading-none" aria-hidden="true" />
                  </span>
                  {topic.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-eye-line text-base leading-none" aria-hidden="true" />
                  </span>
                  {formatCount(topic.reads)} 次阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-chat-3-line text-base leading-none" aria-hidden="true" />
                  </span>
                  {formatCount(topic.comments)} 条讨论
                </span>
              </div>

              <figure className="mt-6">
                <div className="h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
                  <img
                    src={topic.image}
                    alt={`${topic.title} - 热门吃瓜话题封面图`}
                    title={`${topic.title} - 热门吃瓜话题`}
                    className="h-full w-full object-top"
                  />
                </div>
                <figcaption className="mt-2 text-xs text-foreground-500">
                  配图为主题意象图，用于区分话题分类，与事件本身无直接关联。
                </figcaption>
              </figure>

              <div className="mt-8 flex flex-col gap-5">
                <h2 className="sr-only">事件详情</h2>
                <p className="rounded-lg border-l-2 border-primary-400 bg-background-100/70 px-4 py-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  {topic.summary}
                </p>
                {topic.body.map((paragraph) => (
                  <p key={paragraph.slice(0, 16)} className="text-sm leading-[1.9] text-foreground-700 md:text-base">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">相关标签：</span>
                {topic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to="/rank"
                    className="cursor-pointer rounded-full bg-background-200/70 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <section className="mt-10 lg:hidden" aria-labelledby="timeline-mobile-title">
                <h2 id="timeline-mobile-title" className="heading-font text-lg font-bold text-foreground-950">
                  事件脉络
                </h2>
                <ol className="mt-4 flex flex-col gap-0">
                  {topic.timeline.map((item, index) => (
                    <li key={item.text} className="relative flex gap-4 pb-5 last:pb-0">
                      <span className="flex flex-col items-center">
                        <span className="flex h-3 w-3 items-center justify-center rounded-full bg-primary-500" />
                        {index < topic.timeline.length - 1 && (
                          <span className="mt-1 w-px flex-1 bg-background-300" />
                        )}
                      </span>
                      <span className="flex flex-col">
                        <span className="text-xs font-medium text-primary-700">{item.time}</span>
                        <span className="mt-1 text-sm leading-relaxed text-foreground-700">{item.text}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </section>

              <section className="mt-12" aria-labelledby="related-title">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 id="related-title" className="heading-font text-lg font-bold text-foreground-950 md:text-xl">
                    相关话题推荐
                  </h2>
                  <Link
                    to="/rank"
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm text-primary-700 transition-colors hover:text-primary-800"
                  >
                    更多热议
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-line text-base leading-none" aria-hidden="true" />
                    </span>
                  </Link>
                </div>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {related.map((item) => (
                    <TopicCard key={item.id} topic={item} />
                  ))}
                </div>
              </section>
            </article>

            <aside className="flex w-full flex-col gap-6 lg:col-span-4">
              <section className="hidden rounded-lg border border-background-200 bg-background-100/70 p-5 lg:block" aria-labelledby="timeline-title">
                <h2 id="timeline-title" className="heading-font text-base font-bold text-foreground-950">
                  事件脉络
                </h2>
                <p className="mt-1 text-xs text-foreground-500">按时间顺序还原讨论爆发的过程。</p>
                <ol className="mt-4 flex flex-col">
                  {topic.timeline.map((item, index) => (
                    <li key={item.text} className="relative flex gap-3 pb-5 last:pb-0">
                      <span className="flex flex-col items-center">
                        <span className="flex h-3 w-3 items-center justify-center rounded-full bg-primary-500" />
                        {index < topic.timeline.length - 1 && (
                          <span className="mt-1 w-px flex-1 bg-background-300" />
                        )}
                      </span>
                      <span className="flex flex-col">
                        <span className="text-xs font-medium text-primary-700">{item.time}</span>
                        <span className="mt-1 text-sm leading-relaxed text-foreground-700">{item.text}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </section>

              <section className="rounded-lg border border-background-200 bg-background-100/70 p-5" aria-labelledby="side-hot-title">
                <h2 id="side-hot-title" className="heading-font text-base font-bold text-foreground-950">
                  此刻热榜前五
                </h2>
                <ol className="mt-4 flex flex-col gap-3">
                  {hotList.map((item, index) => (
                    <li key={item.id} className="flex items-start gap-3">
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-black ${
                          index === 0
                            ? "bg-primary-500 text-background-50"
                            : index < 3
                              ? "bg-primary-300 text-foreground-950"
                              : "bg-background-200 text-foreground-700"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1 text-sm leading-snug text-foreground-800">
                        {item.id === topic.id ? (
                          <span className="font-semibold text-primary-700">{item.title}</span>
                        ) : (
                          <Link
                            to={`/topic/${item.id}`}
                            className="cursor-pointer transition-colors hover:text-primary-700"
                          >
                            {item.title}
                          </Link>
                        )}
                        <span className="mt-1 block text-xs text-foreground-500">
                          {formatHeat(item.heat)} 热度
                        </span>
                      </span>
                    </li>
                  ))}
                </ol>
              </section>

              <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h2 className="heading-font flex items-center gap-2 text-base font-bold text-accent-900">
                  <span className="flex h-5 w-5 items-center justify-center">
                    <i className="ri-information-line text-lg leading-none" aria-hidden="true" />
                  </span>
                  阅读提示
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-accent-800/90">
                  站内内容为公开讨论的脉络综述。涉及尚未证实的说法，请以官方与权威渠道的后续通报为准。
                </p>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}